import { Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, Typography } from "@mui/material";
import { styled } from '@mui/material/styles';
import {HighlightOff} from "@mui/icons-material";
import EnhancedSelect from "../../../../../components/EnhancedSelect";
import React from "react";
import { pageContentGap } from '../../../../../theme';
import type {ProductAttribute, VariesBy, VariesByOption} from "../types";

// region TypeScript Interfaces
export interface ProductVariantManagerProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    availableAttributes: ProductAttribute[];
    variesBy: VariesBy;
    setVariesBy: (variesBy: VariesBy) => void;
}
// endregion

const StyledDialog = styled(Dialog)({
    "& .MuiPaper-rounded": {
        borderRadius: "12px",
        padding: "16px",
        overflow: "visible"
    },
    "& .MuiDialogTitle-root": {
        padding: 0
    },
    "& .MuiDialogContent-root": {
        padding: 0
    },
    "& .MuiDialog-container": {
        overflow: "visible"
    }
});

export default function ProductVariantManager(props: ProductVariantManagerProps) {
    const {open, setOpen, availableAttributes, variesBy, setVariesBy} = props;

    const mappedAttributes: VariesByOption[] = React.useMemo(() => {
        return availableAttributes.map((attribute) => ({
            value: attribute.Name,
            label: attribute.FriendlyName
        }))
    }, [availableAttributes]);
    const [localVariesBy, setLocalVariesBy] = React.useState<VariesBy>(variesBy);
    const completeChanges = () => {
        setVariesBy(localVariesBy);
        setOpen(false);
    }

    React.useEffect(() => {
        setLocalVariesBy(variesBy);
    }, [variesBy]);

    return (
        <StyledDialog open={open} onClose={() => setOpen(false)}
                maxWidth={"sm"} fullWidth disablePortal>
            <DialogTitle>
                <Grid container justifyContent={"space-between"} alignItems={"center"}>
                    <Typography style={{fontSize: "20px", fontWeight: "600"}}>Manage Variants</Typography>

                    <IconButton
                        style={{height: "20px", width: "20px"}}
                        onClick={() => setOpen(false)}
                        size="large">
                        <HighlightOff style={{color: "red"}}/>
                    </IconButton>
                </Grid>
            </DialogTitle>
            <DialogContent style={{overflow: "visible"}}>
                <Grid container style={{gap: pageContentGap, marginTop: "24px", flexDirection: "column"}}>
                    <Grid item container>
                        <EnhancedSelect
                            $selectorStyles={{height: "100%"}}
                            controlStyle={{overflow: "auto", maxHeight: "200px"}}
                            chipStyle={{maxWidth: "200px", maxHeight: "25px", background: "white"}}
                            isMulti
                            label={"Varies By"}
                            options={mappedAttributes}
                            value={localVariesBy.options}
                            onChange={(e: VariesByOption[]) => {
                                setLocalVariesBy((prevState) => ({
                                    options: [...e],
                                    fields: [...prevState.fields]
                                }))
                            }}
                        />
                    </Grid>

                    <Grid item container>
                        <EnhancedSelect
                            chipStyle={{maxWidth: "200px", maxHeight: "25px", background: "white"}}
                            $selectorStyles={{height: "100%"}}
                            controlStyle={{overflow: "auto", maxHeight: "200px"}}
                            isMulti
                            label={"Variant Fields"}
                            options={mappedAttributes.filter((attribute) => !localVariesBy.options.includes(attribute))}
                            value={localVariesBy.fields}
                            onChange={(e: VariesByOption[]) => {
                                setLocalVariesBy((prevState) => ({
                                    fields: [...e],
                                    options: [...prevState.options]
                                }))
                            }}
                        />
                    </Grid>

                    <Grid item container style={{gap: "8px"}} justifyContent={"flex-end"}>
                        <Button variant="text" onClick={() => setOpen(false)}>
                            Cancel
                        </Button>

                        <Button
                            color={"primary"} variant={"text"}
                            disabled={(localVariesBy.options.length === 0)}
                            onClick={() => completeChanges()}
                        >
                            Save
                        </Button>
                    </Grid>
                </Grid>
            </DialogContent>
        </StyledDialog>
    );
}
